import { GlobalErrorHandler } from 'app/core/error/global-error-handler';
import { provideHttpClient, withXhr } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, inject } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, Router, withComponentInputBinding, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideFuse } from '@fuse';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { provideAutocompleteOff } from 'app/core/autocomplete/autocomplete-off.service';
import { provideInputSafety } from 'app/core/input-safety/input-safety.service';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import * as Sentry from "@sentry/angular";
import {  AbstractSecurityStorage, provideAuth as provideOAuth } from 'angular-auth-oidc-client';
import { authConfig } from './core/auth/auth.config';
import { TokenSafeStorage } from './core/auth/token-safe-storage.service';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MyCustomPaginatorIntl } from './modules/shared/services/custom-paginator-intl';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(withXhr()),
        provideOAuth({config:authConfig}),
        // Keep tokens and user data in memory instead of the browser's storage (BSI O.Arch_4)
        { provide: AbstractSecurityStorage, useClass: TokenSafeStorage },

        provideRouter(appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({scrollPositionRestoration: 'enabled'}),
            withComponentInputBinding()
        ),

        // Material Table interpolation
        { provide: MatPaginatorIntl, useClass: MyCustomPaginatorIntl },
    
        // Material Date Adapter
        {
            provide : DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide : MAT_DATE_FORMATS,
            useValue: {
                parse  : {
                    dateInput: 'D',
                },
                display: {
                    dateInput         : 'DDD',
                    monthYearLabel    : 'LLL yyyy',
                    dateA11yLabel     : 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },


        // Sentry, behind our own handler that also tells the user in plain words (BSI O.Source_4)
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs      : [
                    {
                        id   : 'de',
                        label: 'German',
                    },
                    {
                        id   : 'en',
                        label: 'English',
                    },
                    // {
                    //     id   : 'tr',
                    //     label: 'Turkish',
                    // },
                ],
                defaultLang         : 'de',
                fallbackLang        : 'en',
                reRenderOnLangChange: true,
                prodMode            : true,
            },
            loader: TranslocoHttpLoader,
        }),
        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide   : APP_INITIALIZER,
            useFactory: () =>
            {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () => firstValueFrom(translocoService.load(defaultLang));
            },
            multi     : true,
        },

        provideAuth(),
        provideIcons(),
        // Browser autocomplete off for every form and text field (BSI O.Data_9, O.Data_18)
        provideAutocompleteOff(),
        // Length limit and control-character filter for every text field (BSI O.Source_1)
        provideInputSafety(),

        // Fuse
        provideFuse({
            // mockApi: {
            //     delay   : 0,
            //     services: mockApiServices,
            // },
            fuse   : {
                layout : 'classy',
                scheme : 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme  : 'theme-default',
                themes : [
                    {
                        id  : 'theme-default',
                        name: 'Default',
                    },
                    {
                        id  : 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id  : 'theme-adele',
                        name: 'Adele',
                    },
                    {
                        id  : 'theme-juh',
                        name: 'Johanniter',
                    }
                ],
            },
        }),
    ],
};
